import queryString from 'query-string';

import type { AccountQueryParams } from './admin/components/pending-admin-action-tabs/account-query-param-filters';

import { AccountUserQueryParams } from './account/components/account-users';
import { ApplicantStatusPageQueryParams } from './job/applicant-status-page';
import { InProgressTabQueryParams } from './job/in-progress-page';

import { ApplicantDirectoryQueryParams } from './applicant-directory/utils/applicant-directory-utils';
import { FilterByJobStatus } from './util/job-status';

const esc = encodeURIComponent;
const query = (params: any) =>
  Object.keys(params)
    .filter((k) => Boolean(params[k]))
    .map((k) => `${esc(k)}=${esc(params[k])}`)
    .join('&');

export const HOME_ROUTE = '/';
export const LOGIN_ROUTE = '/login';
export const LOGGED_OUT_ROUTE = '/loggedout';

export const authorizeServiceRoute = (service?: string) =>
  `/authorize/${service || ':service'}/code`;
export const quickbooksAuthenticationRoute = () => '/quickbooks-authentication';
export const salesforceAccountLookupRoute = () => '/salesforce-account-lookup';

export const homePageLandingRedirectRoute = (queryParams?: AccountQueryParams) =>
  `${HOME_ROUTE}${queryParams ? `?${queryString.stringify(queryParams)}` : ''}`;

/** ACCOUNT ROUTES */

export const accountBaseRoute = (accountPage?: string) =>
  `/account/${accountPage || ':accountPage'}`;

export const ACCOUNT_BILLING_CHECKOUT_REDIRECT = '/account/billing/checkout';
export const ACCOUNT_PROFILE = '/profile';

export const accountRoute = (accountId?: number) => `/accounts/${accountId || ':accountId'}`;
export const accountBilling = (accountId?: number) => `${accountRoute(accountId)}/billing`;
export const accountUser = (accountId?: number, queryParams?: AccountUserQueryParams) =>
  `${accountRoute(accountId)}/user${queryParams ? `?${query(queryParams)}` : ''}`;
export const accountProfile = (accountId?: number) => `${accountRoute(accountId)}/profile`;
export const accountOrganizations = (accountId?: number) =>
  `${accountRoute(accountId)}/organizations`;
export const accountCarrierRegistration = (accountId?: number) =>
  `${accountRoute(accountId)}/carrier-registration`;

/** ************************** DEV ROUTES ************************** */

export const STYLE_GUIDE_DEV_ROUTE = '/dev/style-guide' as const;
export const INBOUND_UTILITY_DEV_ROUTE = '/dev/inbound-utility' as const;
export const INBOUND_TEXT_UTILITY_DEV_ROUTE = '/dev/inbound-text-utility' as const;
export const WHATS_APP_UTILITY_DEV_ROUTE = '/dev/whats-app-utility' as const;
export const HELPSCOUT_REDIRECT_DEV_ROUTE = '/dev/helpscout-redirect/:type' as const;
export const ERROR_TEST_DEV_ROUTE = '/dev/error-test' as const;
export const AI_DEV_ROUTE = '/dev/ai' as const;

/** ********************** ORG ROUTES ************************** */

export const orgRoute = (orgId?: number) => `/org/${orgId || ':orgId'}`;
export const orgSettingsRoute = (orgId: number) => `${orgRoute(orgId)}/settings`;

export const orgAutomationsRoute = (
  orgId?: number,
  queryParams?: {
    jobId?: number | null;
    tab?: string;
    focusApplicantAutomationId?: number | null;
  },
) => `${orgRoute(orgId)}/automations${queryParams ? `?${queryString.stringify(queryParams)}` : ''}`;

export const orgAdminRoute = (
  orgId?: number,
  subpath?: string,
  queryParams?: AccountUserQueryParams,
) =>
  `${orgRoute(orgId)}/admin${subpath ? `/${subpath}` : ''}${
    queryParams ? `?${query(queryParams)}` : ''
  }`;

export const eventCalendarRoute = (orgId?: number) => `${orgRoute(orgId)}/calendar`;

/** ************************** JOB PROCESS ROUTES ************************** */

// [IF 2019] Changed the scheme from root/job/:jobId/ to root/org/:orgId/jobs/:jobId/
// [SS 01/23/2024] Danny loves this route we're never getting rid of it, but also it's very helpful for engineers
export const directJobPageRoute = (jobId?: number) => `/job/${jobId || ':jobId'}`;

export const applicantDirectoryRoute = (queryParams?: ApplicantDirectoryQueryParams) =>
  `/applicant-search${queryParams ? `?${query(queryParams)}` : ''}`;

// ORG LEVEL JOB PROCESS ROUTES
export const orgJobsRoute = (orgId?: number) => `${orgRoute(orgId)}/jobs`;
export const orgMessagesRoute = (
  orgId?: number,
  queryParams?: {
    jobApplicantId?: number | null;
    messageThreadPreviewScrollPosition?: number | null;
    jobStatus?: FilterByJobStatus;
  },
) => `${orgJobsRoute(orgId)}/messages${queryParams ? `?${query(queryParams)}` : ''}`;
export const orgNeedsReviewRoute = (
  orgId?: number,
  queryParams?: { jobStatus?: FilterByJobStatus },
) => `${orgJobsRoute(orgId)}/need-review${queryParams ? `?${query(queryParams)}` : ''}`;
export const orgInProgressRoute = (orgId?: number, applicantParams?: InProgressTabQueryParams) =>
  `${orgJobsRoute(orgId)}/in-progress${applicantParams ? `?${query(applicantParams)}` : ''}`;
export const orgSavedRoute = (orgId?: number, queryParams?: ApplicantStatusPageQueryParams) =>
  `${orgJobsRoute(orgId)}/saved${queryParams ? `?${query(queryParams)}` : ''}`;
export const orgRejectedRoute = (orgId?: number, queryParams?: ApplicantStatusPageQueryParams) =>
  `${orgJobsRoute(orgId)}/rejected${queryParams ? `?${query(queryParams)}` : ''}`;
export const orgExpiredRoute = (orgId?: number, queryParams?: ApplicantStatusPageQueryParams) =>
  `${orgJobsRoute(orgId)}/expired${queryParams ? `?${query(queryParams)}` : ''}`;
export const orgApplicantsRoute = (orgId?: number, queryParams?: ApplicantDirectoryQueryParams) =>
  `${orgJobsRoute(orgId)}/applicants${queryParams ? `?${query(queryParams)}` : ''}`;

// JOB LEVEL JOB PROCESS ROUTES
export const jobPageRoute = (orgId?: number, jobId?: number) =>
  `${orgJobsRoute(orgId)}/${jobId || ':jobId'}`;
export const jobNeedReviewRoute = (
  orgId?: number,
  jobId?: number,
  queryParams?: {
    jobApplicantId?: string | number;
    sourceName?: string;
    sortBy?: string;
  },
) => `${jobPageRoute(orgId, jobId)}/need-review${queryParams ? `?${query(queryParams)}` : ''}`;
export const jobMessagesRoute = (
  orgId?: number,
  jobId?: number,
  jobApplicantId?: number | null,
  messageThreadPreviewScrollPosition?: number | null,
) =>
  `${jobPageRoute(orgId, jobId)}/messages${
    jobApplicantId ? `?jobApplicantId=${jobApplicantId}` : ''
  }${
    messageThreadPreviewScrollPosition
      ? `${
          jobApplicantId ? '&' : ''
        }messageThreadPreviewScrollPosition=${messageThreadPreviewScrollPosition}`
      : ''
  }`;
export const jobInProgressRoute = (
  orgId?: number,
  jobId?: number,
  queryParams?: InProgressTabQueryParams & Omit<InProgressTabQueryParams, 'jobStatus'>,
) => `${jobPageRoute(orgId, jobId)}/in-progress${queryParams ? `?${query(queryParams)}` : ''}`;
export const jobSavedRoute = (
  orgId?: number,
  jobId?: number,
  queryParams?: ApplicantStatusPageQueryParams,
) => `${jobPageRoute(orgId, jobId)}/saved${queryParams ? `?${query(queryParams)}` : ''}`;
export const jobRejectedRoute = (
  orgId?: number,
  jobId?: number,
  queryParams?: ApplicantStatusPageQueryParams,
) => `${jobPageRoute(orgId, jobId)}/rejected${queryParams ? `?${query(queryParams)}` : ''}`;
export const jobExpiredRoute = (
  orgId?: number,
  jobId?: number,
  queryParams?: ApplicantStatusPageQueryParams,
) => `${jobPageRoute(orgId, jobId)}/expired${queryParams ? `?${query(queryParams)}` : ''}`;

/** ************************** JOB SETTINGS ROUTES ************************** */
// ORG LEVEL JOB SETTINGS ROUTES
export const orgJobSettingsRoute = (orgId?: number) => `${orgJobsRoute(orgId)}/settings`;
export const orgJobSettingsRecentActivityRoute = (orgId?: number, jobStatus?: FilterByJobStatus) =>
  `${orgJobSettingsRoute(orgId)}/recent-activity${jobStatus ? `?jobStatus=${jobStatus}` : ''}`;
export const orgJobSettingsHistoryRoute = (orgId?: number, jobStatus?: FilterByJobStatus) =>
  `${orgJobSettingsRoute(orgId)}/history${jobStatus ? `?jobStatus=${jobStatus}` : ''}`;
export const orgJobSettingsNotificationsRoute = (orgId?: number, jobStatus?: FilterByJobStatus) =>
  `${orgJobSettingsRoute(orgId)}/notifications${jobStatus ? `?jobStatus=${jobStatus}` : ''}`;

// JOB LEVEL JOB SETTINGS ROUTES
export const jobSettingsBaseRoute = (orgId?: number, jobId?: number) =>
  `${jobPageRoute(orgId, jobId)}/settings`;
export const jobSettingsNotificationsRoute = (orgId?: number, jobId?: number) =>
  `${jobSettingsBaseRoute(orgId, jobId)}/notifications`;
export const jobSettingsHistoryRoute = (orgId?: number, jobId?: number) =>
  `${jobSettingsBaseRoute(orgId, jobId)}/history`;
export const jobSettingsRecentActivityRoute = (orgId?: number, jobId?: number) =>
  `${jobSettingsBaseRoute(orgId, jobId)}/recent-activity`;

/** ************************** JOB DETAILS ROUTES ************************** */
// ORG LEVEL JOB DETAILS ROUTES
export const orgJobDetailsRoute = (orgId?: number) => `${orgJobsRoute(orgId)}/details`;
export const orgJobDetailsPerformanceRoute = (orgId?: number, jobStatus?: FilterByJobStatus) =>
  `${orgJobDetailsRoute(orgId)}/performance${jobStatus ? `?jobStatus=${jobStatus}` : ''}`;

// JOB LEVEL JOB DETAILS ROUTES
export const jobDetailsBaseRoute = (orgId?: number, jobId?: number) =>
  `${jobPageRoute(orgId, jobId)}/details`;
export const jobDetailsPreviewRoute = (orgId?: number, jobId?: number, isEditing?: boolean) =>
  `${jobDetailsBaseRoute(orgId, jobId)}/preview${
    jobId && isEditing ? `?${query({ isEditing })}` : ''
  }`;
export const jobDetailsPerformanceRoute = (orgId?: number, jobId?: number) =>
  `${jobDetailsBaseRoute(orgId, jobId)}/performance`;

/** ************************** EMPLOYEE ROUTES ************************** */

export const employeesRoute = (orgId?: number, queryParams?: any) =>
  `${orgRoute(orgId)}/employees${queryParams ? `?${query(queryParams)}` : ''}`;
export const employeeGroupsRoute = (orgId?: number) => `${employeesRoute(orgId)}/groups`;
export const multiOrgIntegrationConfigRoute = (orgId?: number) =>
  `${employeesRoute(orgId)}/multi-org-integration-config`;
export const employeeCommunicationRoute = (orgId?: number) =>
  `${employeesRoute(orgId)}/communication`;
export const employeeMessagesRoute = ({
  orgId,
  employeeId,
  groupId,
  messageThreadPreviewScrollPosition,
}: {
  orgId?: number;
  employeeId?: number | null;
  groupId?: number | string | null;
  messageThreadPreviewScrollPosition?: number | null;
}) =>
  `${employeeCommunicationRoute(orgId)}/messages${
    employeeId || groupId
      ? `?${queryString.stringify({ employeeId, groupId, messageThreadPreviewScrollPosition })}`
      : ''
  }`;
export const employeeAnnouncementsRoute = (orgId?: number, groupId?: string | number | null) =>
  `${employeeCommunicationRoute(orgId)}/announcements${groupId ? `?groupId=${groupId}` : ''}`;
export const employeeRoute = (orgId?: number, employeeId?: number) =>
  `${employeesRoute(orgId)}/${employeeId || ':employeeId'}`;
export const employeeReferralsRoute = (orgId?: number) => `${employeesRoute(orgId)}/referrals`;
export const surveysRoute = (orgId?: number, surveyId?: number | null) =>
  `${employeesRoute(orgId)}/surveys/${
    surveyId === undefined ? ':surveyId?' : surveyId === null ? '' : surveyId
  }`;

/** ************************** APPLICANT ROUTES ************************** */

export const jobApplicationPageRoute = (jobApplicantId?: number, tab?: string) =>
  `/application/${jobApplicantId || ':jobApplicantId'}${tab ? `?${query({ tab })}` : ''}`;
